import { useMemo } from "react";
import { ApacCheckoutComponent, useApacCheckoutStyles } from "@apac/checkout";
import { PassengerName, DeleteTravelerDialog } from "@apac/components/checkout";
import type { Person } from "@b2bportal/air-booking-api";
import { useModuleBEM } from "@b2bportal/core-themes";
import type { Traveler } from "@b2bportal/traveler-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import type { TravelProductEnum } from "@hopper-b2b/types";
import { Checkbox } from "@tenant/ui";
import clsx from "clsx";
import defaultStyles from "./PassengerRow.module.scss";

type Passenger = Person | Traveler;

export interface PassengerRowProps {
  className?: string;
  passenger: Passenger;
  selected?: boolean;
  onClick: (person: Passenger) => void;
  disabled?: boolean;
  handleEdit: (passengerId: string) => void;
  handleDelete: (passengerId: string) => void;
  funnell?: TravelProductEnum | "TravelerBook";
}

export const PassengerRow = ({
  className,
  passenger,
  selected = false,
  onClick,
  disabled = false,
  handleEdit,
  handleDelete,
  funnell,
}: PassengerRowProps): JSX.Element => {
  const COMPONENT_KEY = ApacCheckoutComponent.PassengerRow;
  const styles = useApacCheckoutStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const { t } = useI18nContext();
  const isReadOnly = useMemo(() => funnell === "TravelerBook", [funnell]);

  return (
    <li
      key={passenger.id}
      className={clsx(
        className,
        block,
        cn("root", {
          disabled,
          selected,
          readOnly: isReadOnly,
        })
      )}
    >
      {/* TODO: Handle Car and Hotel case in here, or RadioGroup */}
      {isReadOnly ? null : (
        <Checkbox
          id={`checkbox-${passenger.id}`}
          checked={selected}
          onCheckedChange={() => {
            onClick(passenger);
          }}
          aria-labelledby={`passenger-${passenger.id}-name`}
          disabled={disabled}
        />
      )}

      <div className={cn("checkbox-label")}>
        <label className={cn("passenger")} htmlFor={`checkbox-${passenger.id}`}>
          <PassengerName
            className={cn("passenger-name")}
            passenger={passenger as Person}
            funnell={funnell}
          />
        </label>
        {!disabled ? (
          <div className={cn("passenger-actions")}>
            <button
              className={cn("passenger-button", { edit: true })}
              onClick={() => handleEdit(passenger.id)}
            >
              {t("edit")}
            </button>
            <div className={cn("button-divider")}></div>

            <DeleteTravelerDialog
              text={{}}
              onConfirmDelete={() => handleDelete(passenger.id)}
              trigger={
                <button className={cn("passenger-button", { delete: true })}>
                  {t("delete")}
                </button>
              }
            />
          </div>
        ) : null}
      </div>
    </li>
  );
};
